import { forwardRef, MouseEvent, useCallback } from 'react';

import { twMerge } from 'tailwind-merge';

const SIZE_STYLE = {
  sm: 'h-5 w-5 checked:bg-[length:0.7em_0.7em] rounded-md',
  md: 'h-6 w-6 checked:bg-[length:0.8em_0.8em] rounded-lg',
  lg: 'h-8 w-8 checked:bg-[length:1em_1em] rounded-xl border-2',
};

type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  readonly size?: 'sm' | 'md' | 'lg';
  readonly onValueChange?: (selected: boolean, value?: string | number | readonly string[]) => void;
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(props, ref) {
  const { children, className, size = 'sm', onValueChange, onClick, ...rest } = props;

  const onClickHandler = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      onValueChange?.(!(rest.checked ?? rest.defaultChecked), rest.value ?? rest.defaultValue);
      onClick?.(e);
    },
    [onClick, onValueChange, rest.value, rest.defaultValue, rest.checked, rest.defaultChecked],
  );

  return (
    <div className={twMerge('flex items-center', className)}>
      <input
        ref={ref}
        type="checkbox"
        {...rest}
        onClick={onClickHandler}
        className={twMerge(
          'peer cursor-pointer border-neutral-400 text-blue-500',
          'dark:border-neutral-700 dark:bg-neutral-700',
          'hover:bg-neutral-100 checked:hover:bg-blue-400',
          'focus-ring focus:ring-offset-0',
          'disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-300 disabled:checked:bg-neutral-300',
          SIZE_STYLE[size],
        )}
      />
      {children && (
        <label
          htmlFor={props.id}
          className="ml-3 peer-disabled:cursor-not-allowed peer-disabled:text-neutral-400 dark:text-neutral-300 peer-disabled:dark:text-neutral-500"
        >
          {children}
        </label>
      )}
    </div>
  );
});

export { Checkbox };
